import { AuthService } from '@account/services/auth.service';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { environment } from 'environments/environment';
import { filter, interval, map, Subscription, takeWhile, tap } from 'rxjs';
import { SessionIdleService } from "@shared/services/session-idle.service";

@Component({
  selector: 'idle-warning-popup',
  templateUrl: './idle-warning-popup.component.html',
  styleUrls: ['./idle-warning-popup.component.scss'],
  standalone: false
})
export class IdleWarningPopupComponent implements OnInit, OnDestroy {

  public visible: boolean = false;
  public sessionExpired: boolean = false;
  public countdown: number = 0;
  public readonly maxCountdown: number = environment.userSession.inactivityLogout - environment.userSession.inactivityWarning;
  private countdownSubscription?: Subscription;
  private idleService: SessionIdleService = inject(SessionIdleService);
  private authService: AuthService = inject(AuthService);

  public ngOnInit(): void {
    this.idleService.onUserWarningLimit$
      .pipe(
        tap((countdown) => {
          this.countdown = countdown;
        }),
        filter(() => !this.visible && this.countdown > 0),
        tap(() => {
          this.visible = true;
          this.startCountdownTimer();
        })
      )
      .subscribe();
  }

  public ngOnDestroy(): void {
    this.stopCountdownTimer();
  }

  public get formatTime(): string {
    const value = this.countdown ?? 0;
    const minutes = Math.floor(value / 60);
    const seconds = value % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  public get isWarning(): boolean {
    return (this.countdown ?? 0) <= 10;
  }

  public endSession(): void {
    this.stopCountdownTimer();
    this.authService.logout();
  }

  public keepSessionActive(): void {
    this.visible = false;
    this.stopCountdownTimer();
  }

  private startCountdownTimer(): void {
    this.stopCountdownTimer();

    if (this.countdown && this.countdown > 0) {
      this.countdownSubscription = interval(1000)
        .pipe(
          map(() => --this.countdown),
          takeWhile(countdown => countdown > 0)
        )
        .subscribe({
          complete: () => {
            this.sessionExpired = true;
            this.authService.logout();
          }
        });
    } else {
      this.sessionExpired = true;
      this.authService.logout();
    }
  }

  private stopCountdownTimer(): void {
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
      this.countdownSubscription = undefined;
    }
  }
}
